import { forwardRef, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled/macro';
import { motion } from 'framer-motion';

import './Card.css';
import { Icon } from '../common/Icon';
import Tooltip from '../common/Tooltip';
import CountCircle from '../common/CountCircle';
import { ParentSize } from '@visx/responsive';
import { VerifiedCheckmark } from '../svgIcons/cra-verified-checkmark';
import CardCommentOverlay from '../comments/CardCommentOverlay';

const cardLayout = {
  observation: { columns: 'auto', areas: 'card' },
  consequence: { columns: 'max-content auto', areas: 'count card' },
  cause: { columns: 'auto max-content', areas: 'card count', iconColor: 'var(--color-green)' },
};

const StateSvgName = {
  target: 'cra-target',
  hazard: 'cra-hazard',
  breach: 'cra-breach',
  high: 'cra-obs-high',
  low: 'cra-obs-low',
  normal: 'cra-neutral',
  sensor: 'cra-sensor',
};

const StyledCardWrapper = styled.div`
  font-size: 0.78rem;
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  grid-gap: 0.5rem;
  grid-template-columns: ${({ type }) => cardLayout[type].columns};
  grid-template-areas: '${({ type }) => cardLayout[type].areas}';
  --card-icon-color: ${({ type }) => cardLayout[type].iconColor};
`;

const ObservationCard = forwardRef(
  (
    {
      data,
      label,
      isSelected,
      isExpanded,
      onCardClick,
      disabled,
      hovered,
      count,
      onMouseEnter,
      onMouseLeave,
      id,
      type,
      parentWidth,
      parentHeight,
      selected,
      isVerified,
      isActive,
    },
    ref
  ) => {
    const { description, state, isSensor, source } = data;
    const [curElTop, setCurElTop] = useState(0);
    const [curElLeft, setCurElLeft] = useState(0);
    const el = useRef(null);

    useEffect(() => {
      setCurElTop(el.current.offsetTop);
      setCurElLeft(el.current.offsetLeft);
    }, [selected]);

    return (
      <div ref={el}>
        <ParentSize>
          {({ width, height }) => {
            const centerOfParent = parentHeight / 2;
            const centerofEl = curElTop + height / 2;
            return (
              <>
                {/* <svg
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: parentWidth,
                    height: parentHeight,
                  }}
                >
                  <Line
                    stroke="#2D2F3C"
                    strokeWidth={2}
                    from={{ y: centerofEl, x: curElLeft }}
                    to={{ x: curElLeft - 15, y: centerofEl }}
                  />
                  <Line
                    stroke="#2D2F3C"
                    from={{ y: centerofEl, x: curElLeft - 15 }}
                    to={{ x: 0, y: centerOfParent }}
                    strokeWidth={2}
                  />
                  <Line
                    stroke="#2D2F3C"
                    from={{ y: centerofEl, x: parentWidth - curElLeft + 15 }}
                    to={{ x: parentWidth - curElLeft, y: centerofEl }}
                    strokeWidth={2}
                  />
                  <Line
                    stroke="#2D2F3C"
                    from={{ x: parentWidth - curElLeft + 15, y: centerofEl }}
                    to={{ x: parentWidth, y: centerOfParent }}
                    strokeWidth={2}
                  />
                </svg> */}
                <button
                  className={'observations-button-wrapper'}
                  type="button"
                  onClick={() => onCardClick(id)}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  disabled={disabled}
                  data-id={id}
                  ref={ref}
                  style={{ position: 'relative' }}
                >
                  {type === 'observation' && id && <CardCommentOverlay functionId={id} />}
                  <StyledCardWrapper type={type}>
                    <div
                      className={`card ${isSelected ? 'card-selected' : 'card-unselected'} ${isExpanded ? 'card-expanded' : ''
                        } ${hovered ? 'card-hovered' : ''}
                      ${isActive ? 'is-active' : ''}
                      `}
                    >
                      <div className={disabled ? 'card-state-grey' : 'card-state'}>
                        {isVerified ? (
                          <VerifiedCheckmark />
                        ) : (
                          <Icon
                            name={StateSvgName[state]}
                            className="card-state__icon"
                            height="40"
                            width="40"
                            fill="currentColor"
                          />
                        )}
                      </div>
                      <Tooltip message={description} label={label}>
                        <div className="card-body text-ellipsis">
                          <span className="text-bold text-ellipsis">{label}</span>
                          <div className="text-small color-gray">
                            <div className="text-ellipsis">{description}</div>
                          </div>
                        </div>
                      </Tooltip>

                      <div className="sensor-icon">
                        {isSensor && (
                          <Tooltip message={'SENSOR READING'}>
                            <Icon
                              name={StateSvgName.sensor}
                              className="card__sensor-icon"
                              height="25"
                              width="25"
                              fill="currentColor"
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {count && <CountCircle count={count} />}
                  </StyledCardWrapper>
                </button>
              </>
            );
          }}
        </ParentSize>
      </div>
    );
  }
);

ObservationCard.displayName = 'Observation Card';
export default motion(ObservationCard);
